import { useState, useEffect } from 'react';
import { X, Megaphone } from 'lucide-react';
import { useAuthStore } from '../store/useStore';
import { announcements, Announcement } from '../lib/api';

// Simple markdown to HTML conversion for announcements
function renderMarkdown(content: string): string {
  return content
    // Bold
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    // Italic
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    // Links
    .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-primary-600 hover:underline">$1</a>')
    // Line breaks
    .replace(/\n\n/g, '</p><p class="mt-2">')
    .replace(/\n/g, '<br />')
    // Wrap in paragraph
    .replace(/^/, '<p>')
    .replace(/$/, '</p>');
}

export default function AnnouncementDisplay() {
  const token = useAuthStore((state) => state.token);
  const [activeAnnouncements, setActiveAnnouncements] = useState<Announcement[]>([]);
  const [currentModal, setCurrentModal] = useState<Announcement | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      loadAnnouncements();
    }
  }, [token]);

  async function loadAnnouncements() {
    try {
      setLoading(true);
      const data = await announcements.getActive(token!);
      setActiveAnnouncements(data.announcements);

      // Show the highest priority modal announcement
      const modalAnnouncement = data.announcements.find(a => a.type === 'modal');
      if (modalAnnouncement) {
        // Small delay so it doesn't compete with other modals
        setTimeout(() => setCurrentModal(modalAnnouncement), 500);
      }
    } catch (error) {
      console.error('Failed to load announcements:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleDismiss(announcementId: string) {
    try {
      await announcements.dismiss(token!, announcementId);
      setActiveAnnouncements(prev => prev.filter(a => a.id !== announcementId));
      if (currentModal?.id === announcementId) {
        // Show next modal if there is one
        const remaining = activeAnnouncements.filter(a => a.id !== announcementId);
        const nextModal = remaining.find(a => a.type === 'modal');
        setCurrentModal(nextModal || null);
      }
    } catch (error) {
      console.error('Failed to dismiss announcement:', error);
    }
  }

  function handleCloseModal() {
    if (currentModal) {
      handleDismiss(currentModal.id);
    }
  }

  if (loading || !currentModal) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity"
        onClick={handleCloseModal}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div className="bg-canvas rounded-xl shadow-xl max-w-md w-full pointer-events-auto overflow-hidden border border-edge">
          {/* Header with gradient */}
          <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-6 text-white relative">
            <button
              onClick={handleCloseModal}
              className="absolute top-4 right-4 p-1 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3 mb-2">
              <Megaphone className="w-8 h-8" />
              <h2 className="text-xl font-bold">{currentModal.title}</h2>
            </div>
            {currentModal.createdBy && (
              <p className="text-purple-100 text-sm">
                From {currentModal.createdBy.displayName || currentModal.createdBy.username}
              </p>
            )}
          </div>

          {/* Content */}
          <div className="p-6">
            <div
              className="prose prose-sm max-w-none text-ink-secondary"
              dangerouslySetInnerHTML={{ __html: renderMarkdown(currentModal.content) }}
            />
          </div>

          {/* Footer */}
          <div className="px-6 pb-6">
            <button
              onClick={handleCloseModal}
              className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
            >
              Got it
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// Export notification announcements hook for use in NotificationPanel
export function useNotificationAnnouncements() {
  const token = useAuthStore((state) => state.token);
  const [notifications, setNotifications] = useState<Announcement[]>([]);

  useEffect(() => {
    if (token) {
      loadNotifications();
    }
  }, [token]);

  async function loadNotifications() {
    try {
      const data = await announcements.getActive(token!);
      setNotifications(data.announcements.filter(a => a.type === 'notification'));
    } catch (error) {
      console.error('Failed to load notification announcements:', error);
    }
  }

  async function dismiss(id: string) {
    try {
      await announcements.dismiss(token!, id);
      setNotifications(prev => prev.filter(a => a.id !== id));
    } catch (error) {
      console.error('Failed to dismiss announcement:', error);
    }
  }

  return { notifications, dismiss };
}
